import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  notification,
  Row,
  Select,
  Space,
  Spin,
  Table,
  TableColumnType,
  Tag,
  Tooltip,
} from 'antd';
import {PlusCircleOutlined, MinusCircleOutlined, SearchOutlined} from '@ant-design/icons';
import PageTitle from '../components/PageTitle';
import PageLoading from '../components/PageLoading';
import {ColumnsType} from 'antd/es/table';
import {FilterDropdownProps} from 'antd/es/table/interface';
import {productsApi} from '../apiCalls/productsApi';
import {
  calculateMgPlus,
  calculateMissingMg,
  calculatePercentageFormula,
  calculatePercentageVRN,
  calculateSecondaryActiveMg,
  resolveVrnBase,
} from '../helper/formulas';
import SelectComponent from '../components/Select';
import {FormulationItemPayload} from '../apiCalls/productPayload';
import {UpOutlined, DownOutlined} from '@ant-design/icons';
import SelectPackaging from '../components/SelectPackaging';
import {useNavigate} from 'react-router-dom';
import {useUser} from '../UserContext';
import {ActiveBreakdown, ActiveDetail, extractActiveDetails, resolveMainActiveFields} from '../helper/actives';
import TableFiltersBar, {applyFiltersToColumns, useTableFilters} from '../components/TableFilters';
import './FormulationTable.css';

interface FormulationItem {
  id: string;
  umbrellaId: string;
  claim: string;
  name: string;
  functionality: string;
  quantity?: number;
  vrn?: number;
  vrnBase?: number;
  vrnBaseDisplay?: number | null;
  unitsClaim?: string;
  vrnUnit?: string;
  vrnChildren?: number;
  vrnChildrenUnit?: string;
  iuClaim?: number;
  ul?: number;
  ulUnit?: string;
  overdosage?: number;
  mgPlus?: number;
  formulaPercentage?: number;
  vrnPercentage?: number | string;
  limitSolids?: number;
  limitSolidsUnit?: string;
  limitLiquids?: number;
  limitLiquidsUnit?: string;
  limitSyrupGels?: number;
  limitSyrupGelsUnit?: string;
  rdi?: number;
  rdiUnit?: string;
  mainActiveValue?: number;
  activesNames?: string;
  mainActiveName?: string;
  activeDetails?: ActiveDetail[];
  activeBreakdown?: ActiveBreakdown[];
  locked: boolean;
  declarableClaim: boolean;
  mainActiveDeclarable: boolean;
  withoutActives: boolean;
}

interface IngredientItem {
  id: string;
  umbrellaId: string;
  claim: string;
  name: string;
  functionality: string;
  mainActiveValue: number;
}

const commonCellProps = () => ({
  style: {padding: 5},
});

const compactCellProps = () => ({
  style: {padding: '5px 4px'},
});

const compactHeaderCellProps = () => ({
  style: {padding: '5px 4px'},
});

const ACTIVE_NAME_COLUMN_TITLE = 'Active #1 Name';
const ACTIVE_NAME_CHAR_WIDTH_PX = 8;
const ACTIVE_NAME_HORIZONTAL_PADDING_PX = 16;
const ACTIVE_NAME_MAX_WIDTH_RATIO = 2.5;

const getMainActiveName = (item: FormulationItem): string => {
  return String(item.mainActiveName ?? item.activeBreakdown?.[0]?.name ?? '');
};

const getAdaptiveActiveNameColumnWidth = (items: FormulationItem[]): number => {
  const titleWidth =
    ACTIVE_NAME_COLUMN_TITLE.length * ACTIVE_NAME_CHAR_WIDTH_PX + ACTIVE_NAME_HORIZONTAL_PADDING_PX;
  const maxWidth = Math.ceil(titleWidth * ACTIVE_NAME_MAX_WIDTH_RATIO);

  const contentWidth = items.reduce((max, item) => {
    const currentWidth = getMainActiveName(item).length * ACTIVE_NAME_CHAR_WIDTH_PX + ACTIVE_NAME_HORIZONTAL_PADDING_PX;
    return Math.max(max, currentWidth);
  }, 0);

  return Math.min(maxWidth, Math.max(titleWidth, contentWidth));
};

const normalizeQuantityToMg = (quantity?: number, unitsClaim?: string): number => {
  const numericQuantity = Number(quantity);
  if (!Number.isFinite(numericQuantity)) {
    return 0;
  }

  if (unitsClaim === 'ug') {
    return numericQuantity / 1000;
  }

  return numericQuantity;
};

const toFiniteNumber = (value: unknown): number | undefined => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const FORMULATION_METRIC_DECIMALS = 5;

const formatTableMetric = (value: unknown): string => {
  const numeric = toFiniteNumber(value);
  if (numeric == null) {
    return '-';
  }

  return numeric.toFixed(FORMULATION_METRIC_DECIMALS);
};

const formatCalcNumber = (value: unknown, decimals = 2): string => {
  const numeric = toFiniteNumber(value);
  if (numeric == null) {
    return '-';
  }

  return numeric
    .toFixed(decimals)
    .replace(/(\.\d*?[1-9])0+$/u, '$1')
    .replace(/\.0+$/u, '');
};

const AddFormulation: React.FC = () => {
  const [allIngredients, setAllIngredients] = useState<IngredientItem[]>([]);
  const [formulationIngredients, setFormulationIngredients] = useState<FormulationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [formulationName, setFormulationName] = useState('');
  const [dosage, setDosage] = useState(1000);
  const [missingMg, setMissingMg] = useState(1000);
  const [mixType, setMixType] = useState('');
  const [formulationId, setFormulationId] = useState('');

  const [searchText, setSearchText] = useState('');
  const [mixCode, setMixCode] = useState(0);
  const [searchedColumn, setSearchedColumn] = useState('');
  const [columns, setColumns] = useState<ColumnsType<FormulationItem>>([]);

  const aggregatedActives = useMemo(() => {
    const totals: Record<
      string,
      {
        name: string;
        totalMgPlus: number;
        totalVrnPercent: number;
        vrnReference?: number;
        occurrences: number;
        totalActiveContributions: string[];
        vrnContributions: string[];
      }
    > = {};

    formulationIngredients.forEach((item) => {
      const actives = item.activeBreakdown ?? [];
      actives.forEach((active, index) => {
        const name = active.name || `Active #${index + 1}`;
        const mgPlus = Number(active.mgPlus ?? 0);
        const quantityClaimInMg = normalizeQuantityToMg(item.quantity, item.unitsClaim ?? 'mg');
        const totalActiveValue = index === 0 ? quantityClaimInMg : mgPlus;
        // "% VRN accumulated":
        // - main active uses the row-level "% VRN" shown in the upper table
        // - secondary actives are calculated from their mg+ and own VRN base
        const rowVrnPercent = Number(item.vrnPercentage);
        const secondaryVrnBase = resolveVrnBase(undefined, active.vrn);
        const secondaryVrnUnit = active.vrnUnit ?? 'mg';
        const secondaryPercentVrn = calculatePercentageVRN(mgPlus, secondaryVrnBase, 'mg', secondaryVrnUnit);
        const numericSecondaryPercentVrn =
          typeof secondaryPercentVrn === 'number' && Number.isFinite(secondaryPercentVrn) ? secondaryPercentVrn : 0;
        const hasMainRowVrn = Number.isFinite(rowVrnPercent);
        const numericPercentVrn =
          index === 0 ? (hasMainRowVrn ? rowVrnPercent : 0) : numericSecondaryPercentVrn;
        const vrnBaseCandidate = index === 0 ? active.vrn ?? item.vrnBase : active.vrn;
        const vrnBase = resolveVrnBase(undefined, vrnBaseCandidate);
        const totalActiveContribution =
          index === 0
            ? `Main (${name}): Quantity (Q) ${formatCalcNumber(item.quantity, 3)} ${item.unitsClaim ?? 'mg'} -> ${formatCalcNumber(totalActiveValue)} mg`
            : `Secondary (${name}): mg+ ${formatCalcNumber(mgPlus)} mg`;
        const vrnContribution =
          index === 0
            ? hasMainRowVrn
              ? `Main (${name}): % VRN row value = ${formatCalcNumber(rowVrnPercent)}`
              : `Main (${name}): no % VRN row value, contributes 0`
            : secondaryVrnBase != null
              ? `Secondary (${name}): (${formatCalcNumber(mgPlus)} mg / ${formatCalcNumber(secondaryVrnBase)} ${secondaryVrnUnit}) x 100 = ${formatCalcNumber(numericSecondaryPercentVrn)}`
              : `Secondary (${name}): no 100% VRN, contributes 0`;

        if (!totals[name]) {
          totals[name] = {
            name,
            totalMgPlus: 0,
            totalVrnPercent: 0,
            occurrences: 0,
            totalActiveContributions: [],
            vrnContributions: [],
          };
        }

        totals[name].totalMgPlus += Number.isFinite(totalActiveValue) ? totalActiveValue : 0;
        totals[name].totalVrnPercent += isNaN(numericPercentVrn) ? 0 : numericPercentVrn;
        totals[name].totalActiveContributions.push(totalActiveContribution);
        totals[name].vrnContributions.push(vrnContribution);
        if (vrnBase != null) {
          const currentVrnReference = totals[name].vrnReference;
          totals[name].vrnReference = currentVrnReference == null ? vrnBase : Math.min(currentVrnReference, vrnBase);
        }
        totals[name].occurrences += 1;
      });
    });

    return Object.values(totals)
      .map((item) => ({
        ...item,
        totalMgPlus: Number(item.totalMgPlus.toFixed(FORMULATION_METRIC_DECIMALS)),
        totalVrnPercent: Number(item.totalVrnPercent.toFixed(FORMULATION_METRIC_DECIMALS)),
        exceedsVrn: item.vrnReference != null && item.totalMgPlus > item.vrnReference,
      }))
      .sort((a, b) => b.totalMgPlus - a.totalMgPlus);
  }, [formulationIngredients]);

  const activeSummaryColumns: ColumnsType<any> = useMemo(
    () => [
      {title: 'Active', dataIndex: 'name'},
      {
        title: 'Total active',
        dataIndex: 'totalMgPlus',
        render: (value: unknown, record: any) => (
          <Tooltip
            title={
              <div>
                <div>Total active = sum of contributions:</div>
                {(record?.totalActiveContributions ?? []).map((line: string, index: number) => (
                  <div key={`${record?.name}-total-${index}`}>{line}</div>
                ))}
                <div>{`Total = ${formatTableMetric(record?.totalMgPlus)} mg`}</div>
              </div>
            }
          >
            <span>{formatTableMetric(value)}</span>
          </Tooltip>
        ),
      },
      {title: 'Active unit', dataIndex: 'activeUnit', render: () => 'mg'},
      {
        title: '% VRN accumulated',
        dataIndex: 'totalVrnPercent',
        render: (value: unknown, record: any) => (
          <Tooltip
            title={
              <div>
                <div>% VRN accumulated = sum of % VRN contributions:</div>
                {(record?.vrnContributions ?? []).map((line: string, index: number) => (
                  <div key={`${record?.name}-vrn-${index}`}>{line}</div>
                ))}
                <div>{`Total = ${formatTableMetric(record?.totalVrnPercent)}%`}</div>
              </div>
            }
          >
            <span>{formatTableMetric(value)}</span>
          </Tooltip>
        ),
      },
      {
        title: 'Notes',
        dataIndex: 'notes',
        render: (_value: unknown, record: any) => {
          const noteValue = record.exceedsVrn ? '*' : '';
          if (!record.exceedsVrn) {
            return noteValue;
          }
          return (
            <Tooltip
              title={`Total active (${formatTableMetric(record?.totalMgPlus)} mg) is greater than 100% VRN (${formatTableMetric(record?.vrnReference)} mg).`}
            >
              <span>{noteValue}</span>
            </Tooltip>
          );
        },
      },
      {title: 'Occurrences', dataIndex: 'occurrences'},
    ],
    [],
  );

  const [form] = Form.useForm();
  const searchInput = useRef<any>(null);
  const navigate = useNavigate();
  const {
    filters: ingredientFilters,
    handleTableChange: handleIngredientTableChange,
    clearAllFilters: clearIngredientFilters,
    removeFilterValue: removeIngredientFilterValue,
  } = useTableFilters<IngredientItem>();

  const requiredField = [
    {
      required: true,
      message: ``,
    },
  ];

  useEffect(() => {
    form.setFieldValue('mixCode', mixType + mixCode);
  }, [mixType, mixCode]);

  useEffect(() => {
    const getCode = async () => {
      const mixCodeRes = await productsApi.getMixCode();
      setMixCode(mixCodeRes.mixCode);
    };
    getCode();
  }, []);

  const handleSearch = (selectedKeys: string[], confirm: FilterDropdownProps['confirm'], dataIndex: string) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const toNumberOrZero = (value: unknown): number => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex: string): TableColumnType<any> => ({
    filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
      <div style={{padding: 8}} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{marginBottom: 8, display: 'block'}}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{width: 90}}
          >
            Buscar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              confirm({closeDropdown: false});
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
              close();
            }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => <SearchOutlined style={{color: filtered ? '#1677ff' : undefined}} />,
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const selectIngredientColumn: ColumnsType<IngredientItem> = [
    {
      title: 'ID',
      dataIndex: 'umbrellaId',
      ...getColumnSearchProps('umbrellaId'),
    },
    {
      title: 'Claim',
      dataIndex: 'claim',
      ...getColumnSearchProps('claim'),
    },
    {
      title: 'Description',
      dataIndex: 'name',
      ...getColumnSearchProps('name'),
    },
    {
      title: 'Functionality',
      dataIndex: 'functionality',
      ...getColumnSearchProps('functionality'),
    },
    {
      title: '',
      dataIndex: 'add',
      render: (value, item) => (
        <a
          onClick={() => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            setFormulationIngredients((prevItems) => [...prevItems, item]);
            setIsModalVisible(false);
          }}
        >
          <PlusCircleOutlined style={{fontSize: 18}} />
        </a>
      ),
    },
  ];
  const selectIngredientColumns = applyFiltersToColumns(selectIngredientColumn, ingredientFilters);

  const getActiveDetailsForItem = (item: FormulationItem): ActiveDetail[] => {
    return item.activeDetails && item.activeDetails.length > 0 ? item.activeDetails : extractActiveDetails(item);
  };

  const applyMainActiveFields = (item: FormulationItem, mainActive?: ActiveDetail): FormulationItem => {
    const meta = resolveMainActiveFields(item, mainActive);
    const vrnValue = resolveVrnBase(undefined, meta.vrn ?? item.vrn);
    const ulValue = meta.ul ?? item.ul;
    const vrnBaseDisplay = vrnValue ?? null;
    const vrnBase = vrnValue;

    return {
      ...item,
      vrn: vrnValue,
      vrnUnit: meta.vrnUnit ?? item.vrnUnit ?? 'mg/day',
      vrnChildren: meta.vrnChildren ?? item.vrnChildren,
      vrnChildrenUnit: meta.vrnChildrenUnit ?? item.vrnChildrenUnit ?? 'mg/day',
      iuClaim: meta.iuClaim ?? item.iuClaim,
      ul: ulValue,
      ulUnit: meta.ulUnit ?? item.ulUnit ?? 'mg/day',
      limitSolids: meta.limitSolids ?? item.limitSolids,
      limitSolidsUnit: meta.limitSolidsUnit ?? item.limitSolidsUnit,
      limitLiquids: meta.limitLiquids ?? item.limitLiquids,
      limitLiquidsUnit: meta.limitLiquidsUnit ?? item.limitLiquidsUnit,
      limitSyrupGels: meta.limitSyrups ?? item.limitSyrupGels,
      limitSyrupGelsUnit: meta.limitSyrupsUnit ?? item.limitSyrupGelsUnit ?? (item as any).limitSyrupsUnit,
      rdi: meta.recDairyIntake ?? item.rdi,
      rdiUnit: meta.recDairyIntakeUnit ?? item.rdiUnit ?? (item as any).recDairyIntakeUnit ?? 'mg/day',
      vrnBase,
      vrnBaseDisplay,
    };
  };

  const renderActivePercentage = (active?: ActiveBreakdown) => {
    if (!active) {
      return '-';
    }

    const hasPercent = Number.isFinite(active.percent);
    const hasMgPlus = active.mgPlus !== undefined && active.mgPlus !== null;

    if (!hasPercent && !hasMgPlus) {
      return '-';
    }

    const pieces: string[] = [];

    if (hasPercent) {
      pieces.push(`${active.percent}%`);
    }

    if (hasMgPlus) {
      pieces.push(`${active.mgPlus} mg+`);
    }

    return pieces.join(' · ');
  };

  const renderOtherActives = (record: FormulationItem) => {
    const others = record.activeBreakdown?.slice(1) ?? [];

    if (!others.length) {
      return '-';
    }

    return (
      <Space size={[4, 4]} wrap>
        {others.map((active, index) => (
          <Tag key={`${record.id}-active-${index + 2}`} color="blue">
            {`${active.name || `Active #${index + 2}`} · ${active.percent ?? 0}% · ${active.mgPlus ?? 0} mg+`}
          </Tag>
        ))}
      </Space>
    );
  };

  const hasDataChanged = (current: FormulationItem[], updated: FormulationItem[]) => {
    if (current.length !== updated.length) {
      return true;
    }

    for (let i = 0; i < current.length; i++) {
      const currentItem = current[i];
      const updatedItem = updated[i];

      if (!updatedItem || currentItem.id !== updatedItem.id) {
        return true;
      }

      if (
        (currentItem.mgPlus ?? 0) !== (updatedItem.mgPlus ?? 0) ||
        (currentItem.formulaPercentage ?? 0) !== (updatedItem.formulaPercentage ?? 0) ||
        String(currentItem.vrnPercentage ?? '') !== String(updatedItem.vrnPercentage ?? '') ||
        (currentItem.mainActiveValue ?? 0) !== (updatedItem.mainActiveValue ?? 0) ||
        (currentItem.mainActiveName ?? '') !== (updatedItem.mainActiveName ?? '')
      ) {
        return true;
      }

      const currentActives = JSON.stringify(currentItem.activeBreakdown ?? []);
      const updatedActives = JSON.stringify(updatedItem.activeBreakdown ?? []);

      if (currentActives !== updatedActives) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const renderForm = (value: any, item: any) => {
    if (typeof value === 'boolean') {
      return <Checkbox checked={value} />;
    }

    if (value?.name) {
      return value.name;
    }

    if (Array.isArray(value) && value.length > 0) {
      if (value[0]?.name) {
        // Si los elementos del array tienen una propiedad 'name', muestra un tooltip para cada uno
        const toolTipContent = value.map((item, index) => {
          return (
            <div
              key={index}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '1000px',
              }}
            >
              {item?.name?.length > 20 ? item?.name?.substring(0, 18) + '...' : item?.name}
            </div>
          );
        });

        return (
          <Tooltip title={<span>{toolTipContent}</span>} overlayStyle={{maxWidth: 1000}}>
            <Tag color="blue">{value.length === 1 ? 'Hay 1 valor' : `Hay ${value.length} valores`}</Tag>
          </Tooltip>
        );
      } else {
        if (item === 'actives') {
          const toolTipContent = value.map((item, index) => {
            const text = `•${item?.activeName?.name} ${item.activeValue}%`;
            return (
              <div
                key={index}
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '1000px',
                }}
              >
                {text}
              </div>
            );
          });

          return (
            <Tooltip title={<span>{toolTipContent}</span>} overlayStyle={{maxWidth: 1000}}>
              <Tag color="blue" key={item}>
                {value.length === 1 ? 'Hay 1 valor' : `Hay ${value.length} valores`}
              </Tag>
            </Tooltip>
          );
        }

        return (
          <Tag color="blue" key={item}>
            {value.length === 1 ? 'Hay 1 valor' : `Hay ${value.length} valores`}
          </Tag>
        );
      }
    }

    return value;
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const allKeys = [...new Set(allIngredients.flatMap(Object.keys))].filter((key) => key !== 'id');
    const activeNameColumnWidth = getAdaptiveActiveNameColumnWidth(formulationIngredients);
    const totalMgPlusForFormula = formulationIngredients.reduce((acc, item) => acc + Number(item.mgPlus ?? 0), 0);
    const createColumns: ColumnsType<FormulationItem> = [
      {
        title: '',
        dataIndex: 'remove',
        render: (value, item) => (
          <a
            onClick={() => {
              handleDelete(item.id);
            }}
          >
            <MinusCircleOutlined style={{fontSize: 18}} />
          </a>
        ),
      },
      {
        title: 'ID',
        dataIndex: 'umbrellaId',
      },
      {
        title: 'Claim',
        dataIndex: 'claim',
      },
      {
        title: 'Description',
        dataIndex: 'name',
      },
      {
        title: 'Functionality',
        dataIndex: 'functionality',
      },
      {
        title: '% Active #1',
        dataIndex: 'mainActiveValue',
        width: 88,
        render: (value, record) =>
          renderActivePercentage(
            record.activeBreakdown?.[0] ?? ({percent: value as number, mgPlus: record.mgPlus} as ActiveBreakdown),
          ),
        onCell: compactCellProps,
        onHeaderCell: compactHeaderCellProps,
      },
      {
        title: 'Active #1 Name',
        dataIndex: 'mainActiveName',
        width: activeNameColumnWidth,
        render: (value, record) => {
          const activeName = value ?? record.activeBreakdown?.[0]?.name ?? '-';
          return (
            <Tooltip title={activeName}>
              <span
                style={{
                  display: 'inline-block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {activeName}
              </span>
            </Tooltip>
          );
        },
        onCell: commonCellProps,
      },
      {
        title: 'mg+',
        dataIndex: 'mgPlus',
        width: 56,
        render: (value: unknown, record: FormulationItem) => (
          <Tooltip
            title={
              <div>
                <div>mg+ is calculated from Quantity (Q), % Active #1 and % Overdosage.</div>
                <div>{`Q = ${formatCalcNumber(record.quantity, 3)} ${record.unitsClaim ?? 'mg'}`}</div>
                <div>{`% Active #1 = ${formatCalcNumber(record.mainActiveValue)}%`}</div>
                <div>{`% Overdosage = ${formatCalcNumber(record.overdosage ?? 0)}%`}</div>
                <div>{`mg+ = ${formatTableMetric(value)}`}</div>
              </div>
            }
          >
            <span>{formatTableMetric(value)}</span>
          </Tooltip>
        ),
        onCell: compactCellProps,
        onHeaderCell: compactHeaderCellProps,
      },
      {
        title: (
          <Tooltip title="Quantity (Q) Claim">
            <span
              style={{
                display: '-webkit-box',
                maxWidth: 72,
                maxHeight: 24,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                lineHeight: '12px',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              Quantity (Q) Claim
            </span>
          </Tooltip>
        ),
        dataIndex: 'quantity',
        width: 90,
        render: (text, record) => (
          <Input
            type="number"
            step={0.001}
            value={text}
            style={{width: 72}}
            onChange={(e) => handleFieldChange(e.target.value, record.id, 'quantity')}
          />
        ),
        onCell: compactCellProps,
        onHeaderCell: compactHeaderCellProps,
      },
      {
        title: 'Units claim',
        dataIndex: 'unitsClaim',
        render: (text, record) => (
          <Select
            defaultValue={'mg'}
            onChange={(value) => {
              handleFieldChange(value, record.id, 'unitsClaim');
            }}
            style={{width: '100%'}}
          >
            <Select.Option value={'mg'}>mg</Select.Option>
            <Select.Option value={'ug'}>ug</Select.Option>
          </Select>
        ),
        onCell: commonCellProps,
      },
      {
        title: (
          <Tooltip title="% Overdosage">
            <span
              style={{
                display: '-webkit-box',
                maxWidth: 70,
                maxHeight: 24,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal',
                lineHeight: '12px',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              % Overdosage
            </span>
          </Tooltip>
        ),
        dataIndex: 'overdosage',
        width: 88,
        render: (text, record) => (
          <Input
            type="number"
            step={0.1}
            value={text}
            style={{width: 68}}
            onChange={(e) => handleFieldChange(e.target.value, record.id, 'overdosage')}
          />
        ),
        onCell: compactCellProps,
        onHeaderCell: compactHeaderCellProps,
      },
      // Commented per request: redundant with main active + other actives.
      // {
      //   title: 'Actives',
      //   dataIndex: 'activesNames',
      //   render: (text) => (
      //     <Tooltip title={text}>
      //       <span
      //         style={{
      //           whiteSpace: 'nowrap',
      //           overflow: 'hidden',
      //           textOverflow: 'ellipsis',
      //           display: 'inline-block',
      //           maxWidth: 220,
      //         }}
      //       >
      //         {text || '-'}
      //       </span>
      //     </Tooltip>
      //   ),
      // },
      {
        title: 'Other Actives',
        dataIndex: 'otherActives',
        render: (_text, record) => renderOtherActives(record),
        onCell: commonCellProps,
      },
      {
        title: '% Formula',
        dataIndex: 'formulaPercentage',
        render: (value: unknown, record: FormulationItem) => (
          <Tooltip
            title={
              <div>
                <div>% Formula = (row mg+ / total mg+) x 100</div>
                <div>{`Row mg+ = ${formatTableMetric(record.mgPlus)}`}</div>
                <div>{`Total mg+ = ${formatTableMetric(totalMgPlusForFormula)}`}</div>
                <div>{`% Formula = ${formatTableMetric(value)}%`}</div>
              </div>
            }
          >
            <span>{formatTableMetric(value)}</span>
          </Tooltip>
        ),
        onCell: commonCellProps,
      },
      {
        title: '% VRN',
        dataIndex: 'vrnPercentage',
        render: (value: unknown, record: FormulationItem) => {
          const vrnBase = resolveVrnBase(undefined, record.vrnBase);
          const hasVrnBase = vrnBase != null;
          const displayNumeric = hasVrnBase ? toFiniteNumber(value) : undefined;
          const displayValue = displayNumeric == null ? '-' : formatTableMetric(displayNumeric);
          const displayPercentText = displayNumeric == null ? '-' : `${formatTableMetric(displayNumeric)}%`;
          const tooltipContent = hasVrnBase ? (
            <div>
              <div>% VRN = (Quantity converted to VRN unit / 100% VRN) x 100</div>
              <div>{`Quantity = ${formatCalcNumber(record.quantity, 3)} ${record.unitsClaim ?? 'mg'}`}</div>
              <div>{`100% VRN = ${formatTableMetric(vrnBase)} ${record.vrnUnit ?? 'mg/day'}`}</div>
              <div>{`% VRN = ${displayPercentText}`}</div>
            </div>
          ) : (
            <div>No 100% VRN defined for main active, % VRN cannot be calculated.</div>
          );

          return (
            <Tooltip title={tooltipContent}>
              <span>{displayValue}</span>
            </Tooltip>
          );
        },
        onCell: commonCellProps,
      },
      {
        title: '100% VRN',
        dataIndex: 'vrnBaseDisplay',
        render: (value: unknown, record: FormulationItem) => {
          const vrnBase = resolveVrnBase(undefined, toFiniteNumber(value));
          return (
            <Tooltip
              title={`Comes from main active VRN (UL is not used). Value: ${formatTableMetric(vrnBase)} ${record.vrnUnit ?? 'mg/day'}`}
            >
              <span>{formatTableMetric(vrnBase)}</span>
            </Tooltip>
          );
        },
        onCell: commonCellProps,
      },
      {
        title: 'VRN unit',
        dataIndex: 'vrnUnit',
        render: (text, record) => <p>{text ?? 'mg/day'}</p>,
        onCell: commonCellProps,
      },
      {
        title: 'I.U. Claim',
        dataIndex: 'iuClaim',
        onCell: commonCellProps,
      },
      {
        title: 'UL',
        dataIndex: 'ul',
        render: (value: unknown) => <span>{formatTableMetric(value)}</span>,
        onCell: commonCellProps,
      },
      {
        title: 'UL unit',
        dataIndex: 'ulUnit',
        render: (text) => text ?? 'mg/day',
        onCell: commonCellProps,
      },
      {
        title: 'RDI',
        dataIndex: 'rdi',
        onCell: commonCellProps,
      },
      {
        title: 'Limit Solids',
        dataIndex: 'limitSolids',
        onCell: commonCellProps,
      },
      {
        title: 'Unit L Solids',
        dataIndex: 'limitSolidsUnit',
        onCell: commonCellProps,
      },
      {
        title: 'Limit Liquids',
        dataIndex: 'limitLiquids',
        onCell: commonCellProps,
      },
      {
        title: 'Unit L Liquids',
        dataIndex: 'limitLiquidsUnit',
        onCell: commonCellProps,
      },
      {
        title: 'Limit Syrup/Gels',
        dataIndex: 'limitSyrupGels',
        onCell: commonCellProps,
      },
      {
        title: 'Unit L Syrup/Gels',
        dataIndex: 'limitSyrupGelsUnit',
        onCell: commonCellProps,
      },
      {
        title: 'Locked',
        dataIndex: 'locked',
        onCell: commonCellProps,
        render: (value, record) => (
          <Checkbox checked={value} onChange={(e) => handleFieldChange(e.target.checked, record.id, 'locked')} />
        ),
        // render: (value: any) => <Checkbox checked={value} />,
      },
      // Removed per request: main Active Declarable merged into Actives column
      {
        title: 'Declarable Claim',
        dataIndex: 'declarableClaim',
        onCell: commonCellProps,
        render: (value, record) => (
          <Checkbox
            checked={value}
            onChange={(e) => handleFieldChange(e.target.checked, record.id, 'declarableClaim')}
          />
        ),
      },
      {
        title: 'without Actives',
        dataIndex: 'withoutActives',
        onCell: commonCellProps,
        render: (value, record) => (
          <Checkbox
            checked={value}
            onChange={(e) => handleFieldChange(e.target.checked, record.id, 'withoutActives')}
          />
        ),
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      ...allKeys
        .filter(
          (item) =>
            ![
              'umbrellaId',
              'actives',
              'activeProducts',
              'claim',
              'vrn',
              'vrnBase',
              'vrnBaseDisplay',
              'iuClaim',
              'ul',
              'ulUnit',
              'vrnChildren',
              'vrnChildrenUnit',
              'name',
              'locked',
              'withoutActives',
              'declarableClaim',
              'mainActiveDeclarable',
              'vrnUnit',
              'limitSolids',
              'limitSolidsUnit',
              'limitLiquids',
              'limitLiquidsUnit',
              'limitSyrupGels',
              'limitSyrupGelsUnit',
              'limitSyrups',
              'rdi',
              'rdiUnit',
              'recDairyIntake',
              'mainActiveValue',
              'mainActiveName',
              'activesNames',
              'unitsClaim',
              'activeDetails',
              'activeBreakdown',
            ].includes(item),
        )
        .map((item) => {
          return {
            title: item,
            key: item,
            dataIndex: item,
            width: 150,
            align: 'center' as const,
            render: (value: any) => renderForm(value, item),
          };
        }),
    ];

    setColumns(createColumns);
  }, [allIngredients, formulationIngredients]);

  useEffect(() => {
    if (formulationIngredients.length === 0) {
      setMissingMg(dosage);
    } else {
      recalculateFormulation(formulationIngredients);
    }
  }, [formulationIngredients, dosage]);

  const handleFieldChange = (value: string | boolean, key: React.Key, column: keyof FormulationItem) => {
    const newData = updateFormulationData(value, key, column);
    recalculateFormulation(newData);
  };

  const updateFormulationData = (value: string | boolean, key: React.Key, column: keyof FormulationItem) => {
    const newData = [...formulationIngredients];
    const index = newData.findIndex((item) => item.id === key);
    const item = newData[index];
    if (item) {
      const newItem = {...item, [column]: value};
      newData.splice(index, 1, newItem);
    }
    return newData;
  };

  const recalculateFormulation = (newData: FormulationItem[]) => {
    const recalculatedData = newData.map((item) => {
      const activeDetails = getActiveDetailsForItem(item);
      const mainActive = activeDetails[0];
      const mainActivePercent = mainActive?.percent ?? item.mainActiveValue ?? 0;

      const mgPlus =
        calculateMgPlus(item.overdosage ?? 0, item.quantity, item.unitsClaim ?? 'mg', mainActivePercent) ?? 0;

      const activeBreakdown: ActiveBreakdown[] = activeDetails.map((active, index) => {
        const percent = active.percent ?? 0;
        const mgPlusValue = index === 0 ? mgPlus : calculateSecondaryActiveMg(mgPlus, percent);
        return {
          ...active,
          percent,
          mgPlus: mgPlusValue,
        };
      });

      const enrichedItem = applyMainActiveFields(item, mainActive);

      return {
        ...enrichedItem,
        mainActiveValue: mainActivePercent,
        mainActiveName: item.mainActiveName ?? mainActive?.name,
        activeDetails,
        activeBreakdown,
        mgPlus,
      };
    });

    const totalMg = recalculatedData.reduce((acc, item) => {
      return acc + Number(item.mgPlus ?? 0);
    }, 0);

    recalculatedData.forEach((item) => {
      item.formulaPercentage = calculatePercentageFormula(item.mgPlus, totalMg);
      item.vrnPercentage = calculatePercentageVRN(
        item.quantity,
        item.vrnBase,
        item.unitsClaim ?? 'mg',
        item?.vrnUnit ?? 'mg',
      );
    });

    const missingMgRes = calculateMissingMg(dosage, totalMg);
    setMissingMg(missingMgRes);

    if (hasDataChanged(newData, recalculatedData)) {
      setFormulationIngredients(recalculatedData);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await productsApi.getFormulationProducts();
      // Do not filter by mainActiveValue. Some products (excipients) do not
      // provide it but must still be selectable. Derive it if possible.
      const products = data.map((item: any) => {
        const activeDetails = extractActiveDetails(item);
        const mainActive = activeDetails[0];
        const mainActiveValue = item?.mainActiveValue ?? mainActive?.percent ?? 0;
        const mainActiveName =
          mainActive?.name || (item?.mainActive && (item?.mainActive?.name || item?.mainActiveName)) || undefined;
        const activesNames = Array.from(
          new Set([mainActiveName, ...activeDetails.map((active: ActiveDetail) => active.name)].filter(Boolean)),
        ).join(', ');

        const baseItem = {
          ...item,
          name: item?.name,
          id: item?.id,
          umbrellaId: item?.umbrellaId,
          functionality: item?.functionality?.name ?? '',
          claim: item?.claim?.name ?? '',
          mainActiveValue,
          mainActiveName,
          activeDetails,
          activesNames,
          vrn: item?.vrn,
          vrnUnit: item?.vrnUnit ?? 'mg',
          unitsClaim: item?.unitClaim ?? 'mg',
          locked: !!item?.locked,
          declarableClaim: !!item?.declarableClaim,
          mainActiveDeclarable: !!item?.mainActiveDeclarable,
          withoutActives: !!item?.withoutActives,
        } as FormulationItem;

        const withMainActive = applyMainActiveFields(baseItem, activeDetails[0]) as any;
        return withMainActive;
      });
      setAllIngredients(products);
    } catch (error) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: string) => {
    setFormulationIngredients(formulationIngredients.filter((item) => item.id !== id));
  };

  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const hour = now.getHours().toString().padStart(2, '0');
    const minute = now.getMinutes().toString().padStart(2, '0');
    return `${year}${month}${day}${hour}${minute}`;
  };

  const submitFormulation = async () => {
    if (formulationIngredients.length === 0) {
      notification.error({
        message: 'Required fields missing',
        description: 'You must fill out all required fields.',
        duration: 3,
      });
      return;
    }

    const formulationItems: FormulationItemPayload[] = formulationIngredients.map((ingredient, index) => ({
      order: index + 1,
      quantity: ingredient.quantity ?? 0,
      unitsClaim: ingredient.unitsClaim ?? 'mg',
      overdosage: ingredient.overdosage ?? 0,
      mgPlus: ingredient.mgPlus ?? 0,
      percentFormula: ingredient.formulaPercentage ?? 0,
      percentVrn: toNumberOrZero(ingredient.vrnPercentage),
      limitSolids: ingredient.limitSolids ?? 0,
      limitLiquids: ingredient.limitLiquids ?? 0,
      limitSyrups: ingredient.limitSyrupGels ?? 0,
      rdi: ingredient.rdi ?? 0,
      product: ingredient.id,
      locked: ingredient.locked,
      declarableClaim: ingredient.declarableClaim,
      mainActiveDeclarable: ingredient.mainActiveDeclarable,
      withoutActives: ingredient.withoutActives,
    }));

    const formValues = form.getFieldsValue();
    // await productsApi.setFormulation(formulationName, formulationId, dosage, missingMg, 'mg', 0, formulationItems);
    await productsApi.setFormulation({
      ingredients: formulationItems,
      dosageValue: dosage,
      missingDosageValue: missingMg,
      dosageName: '',
      price: 0,
      mixType: mixType,
      mixCode: formValues.mixCode,
      appearance: formValues.appearance,
      mixName: formulationName,
      version: getCurrentDateTime(),
      overdosage: formValues.overdosage ?? true,
      controlRelease: formValues.controlRelease ?? true,
      shelfLifeMix: formValues.shelfLifeMix ?? '12',
      date: new Date().toDateString(),
      primaryPackaging: formValues.primaryPackaging,
      secondaryPackaging: formValues.secondaryPackaging,
      terciaryPackaging: formValues.terciaryPackaging,
      shelfLifeReal: formValues.shelfLifeReal,
      shelfLifeEstimated: formValues.shelfLifeEstimated,
      serving: formValues.serving,
      dosageToDisolveIn: formValues.dosageToDisolveIn,
      ph: formValues.ph,

      idCustomer: formValues.idCustomer,
      customer: formValues.Customer,
      mesophilic: formValues.mesophilicAerobics ?? '1000',
      enterobacteriaceae: formValues.enterobacteriaceae ?? '10',
      escherichia: formValues.eColi ?? 'absent',
      salmonella: formValues.SalmonellaSPP ?? 'absent',
      yeast: formValues.yeastAndMoulds ?? '100',
      bulkDensity: formValues.bulkDensityDensity,
      humidity: formValues.humidity,
      solubility: formValues.solubility,
      refractiveIndex: formValues.refractiveIndex,
      poliarimetry: formValues.polarity,
      particleSize200: formValues.particleSize200,
      particleSize355: formValues.particleSize355,
      particleSize500: formValues.particleSizeMinus500,
      particleSizeMoreThan500: formValues.particleSizePlus500,
      flashPoint: formValues.flashPoint,
      alcoholicGrade: formValues.alcoholicGrade,
      identification: formValues.identification ?? 'FTIR Spectrum',
    });

    navigate('/formulations');

    notification.success({
      message: 'Operación exitosa',
      description: 'Tu operación se ha completado con éxito.',
      duration: 3,
    });
  };

  if (loading) {
    return <PageLoading />;
  }

  return (
    <div>
      <Modal
        title={'Add ingredient'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
        }}
        width={'55%'}
        style={{flex: 1}}
        okButtonProps={{
          style: {
            display: 'none',
          },
        }}
      >
        <TableFiltersBar
          columns={selectIngredientColumns}
          filters={ingredientFilters}
          onClearAll={clearIngredientFilters}
          onRemove={removeIngredientFilterValue}
        />
        <Table
          columns={selectIngredientColumns}
          dataSource={allIngredients.filter((ingredient) => {
            return !formulationIngredients.some((item) => item.umbrellaId === ingredient.umbrellaId);
          })}
          onChange={handleIngredientTableChange}
        />
      </Modal>

      <div style={{marginLeft: 50, marginTop: 50}}>
        <PageTitle title="Add New Formulation" />

        <Form
          onFinishFailed={() => {
            notification.error({
              message: 'Required fields missing',
              description: 'You must fill out all required fields.',
              duration: 3,
            });
          }}
          onFinish={submitFormulation}
          form={form}
          style={{marginRight: 50}}
          layout={'vertical'}
        >
          <p style={{fontWeight: 'bold'}}>Portal del cliente</p>

          <Row style={{marginBottom: -15}} gutter={16}>
            <Col span={6}>
              <Form.Item name={'idCustomer'} label="ID Customer">
                <Input placeholder="Enter ID Customer" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item rules={requiredField} name={'Customer'} label="Customer">
                <Input placeholder="Enter Customer" />
              </Form.Item>
            </Col>
          </Row>

          <p style={{fontWeight: 'bold'}}>Portal de MIX</p>

          <Row style={{marginBottom: -15}} gutter={16}>
            <Col span={6}>
              <Form.Item rules={requiredField} name={'mixType'} label="MIX Type">
                <Select
                  onChange={(value) => {
                    setMixType(value);
                  }}
                  style={{width: '100%'}}
                >
                  <Select.Option value={'00'}>Powder</Select.Option>
                  <Select.Option value={'99'}>Liquid</Select.Option>
                  <Select.Option value={'88'}>Maquila</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name={'mixCode'} label="MIX Code">
                <Input
                  // defaultValue={fullMixCode}
                  disabled={true}
                  // value={fullMixCode} // Use the state variable here
                  placeholder="Enter MIX Code"
                  onChange={(e) => setFormulationId(e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item rules={requiredField} name={'formulationName'} label="MIX Name">
                <Input
                  // required={true}
                  placeholder="Enter MIX Name"
                  onChange={(e) => setFormulationName(e.target.value)}
                />
              </Form.Item>
            </Col>

            <Col span={6}>
              <Form.Item name={'version'} label="Version">
                <Input disabled defaultValue={getCurrentDateTime()} />
              </Form.Item>
            </Col>
          </Row>
          <Row style={{marginBottom: -15}} gutter={16}>
            <Col span={6}>
              <Form.Item name={'overdosage'} label="Overdosage">
                <Select
                  defaultValue={true}
                  onChange={(value) => {
                    form.setFieldValue('overdosage', value);
                    // handleFieldChange(value, record.id, 'unitsClaim');
                  }}
                  style={{width: '100%'}}
                >
                  <Select.Option value={true}>Yes</Select.Option>
                  <Select.Option value={false}>no</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name={'shelfLifeMix'} label="Shelf Life Mix">
                <Input placeholder="Enter shelfLifeMix" defaultValue={'12'} addonAfter={'Months'} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name={'controlRelease'} label="Control Release">
                <Select
                  defaultValue={true}
                  onChange={(value) => {
                    form.setFieldValue('controlRelease', value);
                    // handleFieldChange(value, record.id, 'unitsClaim');
                  }}
                  style={{width: '100%'}}
                >
                  <Select.Option value={true}>Yes</Select.Option>
                  <Select.Option value={false}>no</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <div>
            <p style={{fontWeight: 'bold'}}>Final Product info</p>
            <>
              <Row style={{marginBottom: -15}} gutter={16}>
                <Col span={6}>
                  <Form.Item rules={requiredField} name={'primaryPackaging'} label="Primary Packaging">
                    {mixType !== '' ? (
                      <SelectPackaging
                        title={'primaryPackaging'}
                        onChange={(value) => {
                          // setTodo(value);
                        }}
                        defaultValue={''}
                        endpoint={`/lists/packaging/1/${mixType}`}
                        canDoPostRequest={true}
                        mixType={mixType}
                        pst={1}
                      />
                    ) : (
                      <Select />
                    )}
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item rules={requiredField} name={'secondaryPackaging'} label="Secondary Packaging">
                    <SelectPackaging
                      title={'secondaryPackaging'}
                      onChange={(value) => {
                        // setTodo(value);
                      }}
                      defaultValue={''}
                      endpoint={'/lists/packaging/2/00'}
                      canDoPostRequest={true}
                      pst={2}
                      mixType={'00'}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item rules={requiredField} name={'terciaryPackaging'} label="Terciary Packaging">
                    <SelectPackaging
                      title={'terciaryPackaging'}
                      onChange={(value) => {
                        // setTodo(value);
                      }}
                      defaultValue={''}
                      endpoint={'/lists/packaging/3/00'}
                      canDoPostRequest={true}
                      pst={3}
                      mixType={'00'}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row style={{marginBottom: -15}} gutter={16}>
                <Col span={6}>
                  <Form.Item name={'shelfLifeReal'} label="Shelf Life Real">
                    <Input type="number" placeholder="Enter Shelf Life Real" addonAfter={'Months'} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item rules={requiredField} name={'shelfLifeEstimated'} label="Shelf Life Estimated">
                    <Input type="number" placeholder="Enter Shelf Life Estimated" addonAfter={'Months'} />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item rules={requiredField} name={'serving'} label="Dairy Serving">
                    <Input addonAfter={'Shots'} type="number" placeholder="Enter dairy serving" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={'dosageToDisolveIn'} label="Dosage to dissolve in:">
                    <Input
                      addonAfter={'ml'}
                      type="number"
                      placeholder="Enter dosage to dissolve in"
                      // onChange={(e) => setDosageToDissolveIn(e.target.value)}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row style={{marginBottom: -15}} gutter={16}>
                <Col span={6}>
                  <Form.Item name={'appearance'} label="Appearance:">
                    <Input
                      // addonAfter={'%'}
                      type="text"
                      placeholder="Enter appearance"
                      // onChange={(e) => setAppearancePercentage(e.target.value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={'ph'} label="ph:">
                    <Input addonAfter={'50g/L'} type="number" placeholder="Enter pH value" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={'bulkDensityDensity'} label="Bulk Density/Density:">
                    <Input addonAfter={'g/mL'} type="number" placeholder="Enter bulk density" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={'humidity'} label="Humidity:">
                    <Input addonAfter={'%'} type="number" placeholder="Enter humidity percentage" />
                  </Form.Item>
                </Col>
              </Row>

              <Row style={{marginBottom: -15}} gutter={16}>
                <Col span={6}>
                  <Form.Item name={'solubility'} label="Solubility:">
                    <Input addonAfter={'%'} type="number" placeholder="Enter solubility percentage" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={'refractiveIndex'} label="Refractive Index:">
                    <Input addonAfter={'°'} type="number" placeholder="Enter refractive index" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={'polarity'} label="Polarity:">
                    <Input addonAfter={'°'} type="number" placeholder="Enter polarity" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={'particleSize200'} label="Particle Size <200 ug:">
                    <Input addonAfter={'%'} type="number" placeholder="Enter particle size <200 ug" />
                  </Form.Item>
                </Col>
              </Row>

              <Row style={{marginBottom: -15}} gutter={16}>
                <Col span={6}>
                  <Form.Item name={'particleSize355'} label="Particle Size <355 ug:">
                    <Input addonAfter={'%'} type="number" placeholder="Enter particle size <355 ug" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={'particleSizeMinus500'} label="Particle Size < 500 ug:">
                    <Input addonAfter={'°'} type="number" placeholder="Enter Particle Size < 500 ug" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={'particleSizePlus500'} label="Particle Size > 500 ug:">
                    <Input addonAfter={'°'} type="number" placeholder="Enter Particle Size > 500 ug" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={'flashPoint'} label="Flash Point:">
                    <Input addonAfter={'º'} type="number" placeholder="Enter flash point" />
                  </Form.Item>
                </Col>
              </Row>

              <Row style={{marginBottom: -15}} gutter={16}>
                <Col span={6}>
                  <Form.Item name={'alcoholicGrade'} label="Alcoholic Grade">
                    <Input addonAfter={'º'} type="number" placeholder="Enter alcoholic grade" />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item name={'identification'} label="Identification:">
                    <Select
                      onChange={(value) => {
                        form.setFieldValue('identification', value);
                      }}
                      defaultValue={'FTIR Spectrum'}
                      style={{width: '100%'}}
                    >
                      <Select.Option value={'FTIR Spectrum'}>FTIR Spectrum</Select.Option>
                      <Select.Option value={'HPLC'}>HPLC</Select.Option>
                      <Select.Option value={'Organoleptic Panel'}>Organoleptic Panel</Select.Option>
                      <Select.Option value={'Weight Protocol'}>Weight Protocol</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            </>

            <p style={{fontWeight: 'bold'}}>Microbiology</p>

            <Row style={{marginBottom: -15}} gutter={16}>
              <Col span={6}>
                <Form.Item name={'mesophilicAerobics'} label="Mesophilic aerobics ≤">
                  <Input defaultValue={1000} addonAfter={'cfu/g'} type="number" placeholder="Enter ≤ 1000 cfu/g" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name={'enterobacteriaceae'} label="T. Enterobacteriaceae ≤">
                  <Input defaultValue={10} addonAfter={'cfu/g'} type="number" placeholder="Enter ≤ 10 cfu/g" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name={'eColi'} label="Escherichia Coli">
                  <Input defaultValue={'absent'} addonAfter={'/g'} type="text" placeholder="g" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item name={'SalmonellaSPP'} label="Salmonella SPP">
                  <Input defaultValue={'absent'} addonAfter={'/25g'} type="text" placeholder="Enter Salmonella SPP" />
                </Form.Item>
              </Col>
            </Row>

            <Row style={{marginBottom: -15}} gutter={16}>
              <Col span={6}>
                <Form.Item name={'yeastAndMoulds'} label="T. Yeast & Moulds ≤">
                  <Input defaultValue={100} addonAfter={'cfu/g'} type="number" placeholder="Enter T. Yeast & Moulds" />
                </Form.Item>
              </Col>
            </Row>

            <Divider
              orientation="left"
              orientationMargin={0}
              style={{
                marginBottom: 15,
                marginTop: 20,
              }}
            >
              {/*{section.title}*/}
            </Divider>
            <Row style={{marginBottom: -15}} gutter={16}>
              <Col span={6}>
                <Form.Item label="Dosage (mg)">
                  <Input
                    type="number"
                    value={dosage}
                    onChange={(e) => setDosage(parseFloat(e.target.value))}
                    addonAfter={'mg'}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  style={{
                    borderColor: 'red',
                    borderWidth: 5,
                  }}
                  label={
                    <label style={{color: missingMg > 0 ? 'green' : missingMg < 0 ? 'red' : 'black'}}>
                      Missing (mg)
                    </label>
                  }
                >
                  <Input
                    value={missingMg}
                    style={{
                      borderColor: 'red',
                      borderWidth: 5,
                    }}
                    addonAfter={
                      <label style={{color: missingMg > 0 ? 'green' : missingMg < 0 ? 'red' : 'black'}}>mg</label>
                    }
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Button
            style={{marginTop: 10}}
            onClick={() => {
              setIsModalVisible(true);
            }}
          >
            Add ingredient
            <PlusCircleOutlined style={{marginLeft: 15, fontSize: 15}} />
          </Button>

          <div style={{marginTop: 20, overflowX: 'auto', maxHeight: 800}}>
            <Table
              className="formulation-table"
              columns={columns}
              dataSource={formulationIngredients}
              loading={loading}
              scroll={{x: 'max-content'}}
              tableLayout="auto"
            />
          </div>
          {aggregatedActives.length > 0 && (
            <div style={{marginTop: 20}}>
              <h3 style={{marginBottom: 10}}>Actives summary</h3>
              <Table
                size="small"
                pagination={false}
                columns={activeSummaryColumns}
                dataSource={aggregatedActives}
                rowKey={(record) => record.name}
              />
            </div>
          )}
          <div style={{marginTop: 20, marginBottom: 20}}>
            <Button type="primary" htmlType="submit">
              Save formulation
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AddFormulation;
